import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './GradedCardsPopup.css';

const GradedCardsPopup = ({ onClose, username, userSettings }) => {
  const { t } = useTranslation();
  const [cards, setCards] = useState([{
    certificateNumber: '',
    gradingCompany: 'PSA',
    cardDetails: ''
  }]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const gradingCompanies = [
    'PSA',
    'BGS',
    'CGC',
    'TAG',
    'Get Graded',
    'Ace Grading',
    'Other'
  ];

  const handleAddAnother = () => {
    setCards([...cards, {
      certificateNumber: '',
      gradingCompany: 'PSA',
      cardDetails: ''
    }]);
  };

  const handleRemoveCard = (index) => {
    if (cards.length > 1) {
      const newCards = cards.filter((_, i) => i !== index);
      setCards(newCards);
    }
  };

  const handleCardChange = (index, field, value) => {
    const newCards = [...cards];
    newCards[index][field] = value;
    setCards(newCards);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!customerEmail) {
      setSubmitMessage('Please enter your email address');
      return;
    }

    const hasEmptyFields = cards.some(card => 
      !card.certificateNumber || !card.cardDetails
    );

    if (hasEmptyFields) {
      setSubmitMessage('Please fill in all card details');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://buylist.tcgsync.com'}/api/graded-card-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          customerEmail,
          cards
        }),
      });

      if (response.ok) {
        setSubmitMessage('Your graded card enquiry has been submitted successfully! We aim to reply within 48 hours. Please check your spam folder if you don\'t see our response in your inbox.');
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        setSubmitMessage('Failed to submit enquiry. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting graded card enquiry:', error);
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="graded-cards-popup-overlay" onClick={onClose}>
      <div className="graded-cards-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="graded-cards-popup-close" onClick={onClose}>×</button>
        <h2>{t('Graded Card Enquiry')}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="graded-cards-customer-email">
            <label>{t('Your Email Address')} *</label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />
          </div>

          {cards.map((card, index) => (
            <div key={index} className="graded-card-entry">
              <h3>{t('Card')} #{index + 1}</h3>
              
              <div className="graded-card-field">
                <label>{t('Certificate Number')} *</label>
                <input
                  type="text"
                  value={card.certificateNumber}
                  onChange={(e) => handleCardChange(index, 'certificateNumber', e.target.value)}
                  placeholder="e.g., 12345678"
                  required
                />
              </div>

              <div className="graded-card-field">
                <label>{t('Grading Company')} *</label>
                <select
                  value={card.gradingCompany}
                  onChange={(e) => handleCardChange(index, 'gradingCompany', e.target.value)}
                  required
                >
                  {gradingCompanies.map((company) => (
                    <option key={company} value={company}>{company}</option>
                  ))}
                </select>
              </div>

              <div className="graded-card-field">
                <label>{t('Card Details')} *</label>
                <textarea
                  value={card.cardDetails}
                  onChange={(e) => handleCardChange(index, 'cardDetails', e.target.value)}
                  placeholder="e.g., Charizard Base Set 1st Edition PSA 10"
                  rows="3"
                  required
                />
              </div>

              {cards.length > 1 && (
                <button
                  type="button"
                  className="graded-card-remove-button"
                  onClick={() => handleRemoveCard(index)}
                >
                  {t('Remove This Card')}
                </button>
              )}
            </div>
          ))}

          <div className="graded-cards-actions">
            <button
              type="button"
              className="graded-card-add-another-button"
              onClick={handleAddAnother}
            >
              {t('Add Another')}
            </button>

            <button
              type="submit"
              className="graded-card-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('Submitting...') : t('Submit Enquiry')}
            </button>
          </div>

          {submitMessage && (
            <div className={`graded-cards-message ${submitMessage.includes('success') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default GradedCardsPopup;

